"use client";

import { useState, useEffect } from "react";
import { Phone, Mail, Home } from "lucide-react";
import { SITE_CONFIG } from "@/app/lib/site-config";
import { apiFetch } from "@/app/api/api-client";
import { useSearchParams } from "next/navigation";

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormStatus {
  type: "idle" | "loading" | "success" | "error";
  message?: string;
}

export default function ContactClient() {
  const searchParams = useSearchParams();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState<FormStatus>({ type: "idle" });

  useEffect(() => {
    const serviceParam = searchParams?.get("service");
    const productParam = searchParams?.get("product");
    const skuParam = searchParams?.get("sku");
    const packageParam = searchParams?.get("package");
    const subjectParam = searchParams?.get("subject");

    if (subjectParam) {
      setFormData((prev) => ({
        ...prev,
        subject: subjectParam,
        message: prev.message || (packageParam 
          ? `I would like to request a tailored quote and SLA scope for the ${packageParam} maintenance package.` 
          : `Inquiring regarding ${subjectParam}. Please provide quotation details and engineering availability.`),
      }));
      setTimeout(() => {
        document.querySelector('form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else if (packageParam) {
      setFormData((prev) => ({
        ...prev,
        subject: `Custom SLA Package Quote: ${packageParam}`,
        message: `I would like to request a tailored quote and SLA scope for the ${packageParam} maintenance package.`,
      }));
      setTimeout(() => {
        document.querySelector('form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else if (productParam) {
      const productReference = skuParam ? `${productParam} (SKU: ${skuParam})` : productParam;
      setFormData((prev) => ({
        ...prev,
        subject: `Price & availability: ${productParam}`,
        message: `Please confirm the current price and stock availability for ${productReference}.`,
      }));
      setTimeout(() => {
        document.querySelector('form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else if (serviceParam) {
      setFormData((prev) => ({
        ...prev,
        subject: `${serviceParam} + Quote`,
        message: `${serviceParam} + requesting a quote and call back`,
      }));
      setTimeout(() => {
        const formElement = document.querySelector('form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [searchParams]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus({ type: "loading" });

    try {
      const response = await apiFetch("/contact", {
        method: "POST",
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to submit contact form");
      }

      setStatus({
        type: "success",
        message:
          "Thank you! We have received your message. Our team will get back to you soon.",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      // Clear success message after 5 seconds
      setTimeout(() => setStatus({ type: "idle" }), 5000);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
      });
    }
  }

  return (
    <main className="min-h-screen bg-gray-50/30">
      {/* ================= HERO SECTION ================= */}
      <div className="bg-[#0b0d17] text-white py-20 px-4 text-center relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10">
          <Mail className="absolute -top-4 -left-4 w-64 h-64 rotate-12" />
          <div className="absolute top-10 right-10 border-4 border-white rounded-3xl w-32 h-48 rotate-45" />
        </div>

        <div className="relative z-10">
          <h1 className="text-5xl font-extrabold mb-4 tracking-tight">
            Contact Us
          </h1>
          <p className="text-indigo-200 max-w-lg mx-auto text-lg leading-relaxed">
            We'd love to hear from you. The <strong>{SITE_CONFIG.name}</strong>{" "}
            team is always here to chat.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 -mt-12 relative z-20 pb-20">
        {/* ================= INFO CARDS ================= */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <ContactCard
            icon={<Phone className="text-white w-6 h-6" />}
            title="Call Us"
            detail={SITE_CONFIG.phone}
          />
          <ContactCard
            icon={<Mail className="text-white w-6 h-6" />}
            title="Email Us"
            detail={SITE_CONFIG.email}
          />
          <ContactCard
            icon={<Home className="text-white w-6 h-6" />}
            title="Address"
            detail={SITE_CONFIG.address}
          />
        </div>

        {/* ================= MAP & FORM SECTION ================= */}
        <div className="flex flex-col lg:flex-row items-stretch gap-8">
          {/* LEFT: GOOGLE MAP */}
          <div className="flex-1 min-h-[500px] rounded-2xl overflow-hidden shadow-sm border border-gray-200 bg-white p-2">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3650.328502899966!2d90.42328807502575!3d23.806914886604964!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755c75c6f4edaed%3A0x27d198cada031f5e!2sMR%20Tech!5e0!3m2!1sen!2sbd!4v1770167993461!5m2!1sen!2sbd"
              className="w-full h-full rounded-xl"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>

          {/* RIGHT: CONTACT FORM */}
          <div className="flex-1">
            <form
              onSubmit={handleSubmit}
              className="h-full flex flex-col justify-between space-y-5 p-8 border border-gray-200 rounded-2xl bg-white shadow-sm"
            >
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Send a Message
                </h2>
                <p className="text-gray-500 text-sm mb-6">
                  Fill out the form and we'll get back to you within 24 hours.
                </p>

                {/* Status Messages */}
                {status.type === "success" && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 font-medium text-sm">
                      {status.message}
                    </p>
                  </div>
                )}

                {status.type === "error" && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 font-medium text-sm">
                      {status.message}
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-2 tracking-widest">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      minLength={2}
                      maxLength={100}
                      className="w-full border-b-2 border-gray-100 py-3 text-sm focus:outline-none focus:border-indigo-600 transition-colors bg-transparent placeholder:text-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-2 tracking-widest">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@domain.com"
                      className="w-full border-b-2 border-gray-100 py-3 text-sm focus:outline-none focus:border-indigo-600 transition-colors bg-transparent placeholder:text-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-2 tracking-widest">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+880 1234 567890"
                      pattern="[0-9\+\-\s\(\)]{7,20}"
                      className="w-full border-b-2 border-gray-100 py-3 text-sm focus:outline-none focus:border-indigo-600 transition-colors bg-transparent placeholder:text-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-2 tracking-widest">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="How can we help?"
                      minLength={3}
                      maxLength={200}
                      className="w-full border-b-2 border-gray-100 py-3 text-sm focus:outline-none focus:border-indigo-600 transition-colors bg-transparent placeholder:text-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-2 tracking-widest">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Please provide details about your inquiry..."
                      minLength={10}
                      maxLength={5000}
                      className="w-full border-b-2 border-gray-100 py-3 text-sm resize-none focus:outline-none focus:border-indigo-600 transition-colors bg-transparent placeholder:text-gray-300"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={status.type === "loading"}
                className="w-full bg-[#0b0d17] text-white py-5 rounded-xl font-bold uppercase tracking-[0.2em] text-[11px] hover:bg-indigo-700 disabled:opacity-60 transition-all shadow-xl shadow-gray-200 active:scale-[0.98]"
              >
                {status.type === "loading" ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}

// Helper component for the Info Cards
function ContactCard({
  icon,
  title,
  detail,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 flex flex-col items-center text-center transition-all hover:shadow-xl hover:-translate-y-1">
      <div className="bg-[#0b0d17] p-4 rounded-2xl mb-5 shadow-inner">
        {icon}
      </div>
      <h3 className="font-bold text-gray-900 text-lg mb-2">{title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed">{detail}</p>
    </div>
  );
}
